import { useCallback, useEffect, useState } from 'react'
import { Cookies } from 'react-cookie'
import { useRecoilValue } from 'recoil'
import { io, Socket } from 'socket.io-client'

import { meState } from '../../store'
import { SOCKET_MESSAGES } from '../../utils'
import Sentry from '../../utils/Sentry'
import protocol, { BaySocketEventClass } from '../../utils/socket/protocol'


type BaySocketConnecter = (onConnected?: () => void) => void

interface UseBaySocketResult {
  isConnected: boolean
  isConnecting: boolean
  connect: BaySocketConnecter
  io?: Socket
}

type UseBaySocket = (socketURL?: string) => UseBaySocketResult

const useBaySocket: UseBaySocket = (
  socketURL = import.meta.env.VITE_BOOKING_SERVER_URL
) => {
  const me = useRecoilValue(meState)

  const [socket, setSocket] = useState<Socket>()
  const [isConnecting, setIsConnecting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)

  // 이벤트 호출
  const emit = useCallback(
    (event: BaySocketEventClass) => {
      socket?.emit(event.EVENT_KEY!, event.trigger)
    },
    [socket]
  )

  // 이벤트 응답
  const on = useCallback(() => {
    for (const EventClass of Object.values(protocol)) {
      const event = new EventClass()

      socket?.on(
        event.EVENT_KEY!,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (status: string, response: any) => {
          event.logger(status, response)
          event.listener(status, response)
        }
      )
    }
  }, [socket])

  // 소켓 연결
  const connect: BaySocketConnecter = useCallback(
    (onConnected) => {
      if (!socket) return

      socket.connect()

      setIsConnecting(true)

      socket.on(SOCKET_MESSAGES.CONNECTED, () => {
        console.log('✅ Socket is connected successfully.')

        emit(new protocol.GridSizeEvent())
        emit(new protocol.AllMachineEvent())

        onConnected?.()

        setIsConnecting(false)
        setIsConnected(true)
      })

      socket.on(SOCKET_MESSAGES.DISCONNECTED, () => {
        console.log('❌ Socket is disconnected.')

        Sentry.captureException(Error('타석 연결 끊김'))

        setIsConnected(false)
      })

      on()
    },
    [emit, on, socket]
  )

  useEffect(() => {
    if (!me) return

    const cookies = new Cookies()

    setSocket(
      io(socketURL, {
        transports: ['websocket'],
        reconnectionAttempts: 3,
        autoConnect: false,
        auth: {
          shopID: me.shopID,
          accessToken: cookies.get('accessToken'),
          refreshToken: cookies.get('refreshToken'),
        },
      })
    )
  }, [me, socketURL])

  return {
    isConnecting,
    isConnected,
    io: socket,
    connect,
  }
}

export default useBaySocket
